// components/AccountLoginDialog.jsx
import React, { useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { FaTimes, FaUser, FaPhoneAlt, FaKey } from "react-icons/fa";
import { motion } from "framer-motion";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
// import LanguageChanger from "../../components/LanguageChenger.jsx";
import { useTranslation } from "react-i18next";
// import { t } from "i18next";
// import { verifyGSTService } from "../../services/sale.service";
import { verifyGstForOnlineStore } from "../../services/customer.service";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  sendWhatsAppOtpService,
  whatsappLoginService,
} from "../../services/resellerCustomer.service";
import { getFingerprint } from "../../helpers/fingerprint";
import { resolveThemeId } from "./themeTemplates/storefrontThemePresets";

// IMPORT the cart service functions
import { addToUserCart } from "../../services/cart.service";

// Light theme (default)
const lightTheme = createTheme({
 palette: {
 primary: { main: "#2563eb" }, // blue-600
 secondary: { main: "#f43f5e" }, // rose-500
 },
 typography: {
 fontFamily: "'Poppins', sans-serif",
 },
 components: {
 MuiDialog: {
 styleOverrides: {
 paper: {
 borderRadius: 20,
 paddingTop: 8,
 boxShadow: "0px 10px 30px rgba(0,0,0,0.15)",
 },
 },
 },
 MuiButton: {
 styleOverrides: {
 root: {
 borderRadius: 10,
 textTransform: "none",
 fontWeight: 600,
 },
 },
 },
 },
});

// Midnight dark theme
const midnightTheme = createTheme({
 palette: {
   mode: "dark",
   primary: { main: "#a78bfa" },
   secondary: { main: "#c084fc" },
   background: {
     default: "#0f0f1a",
     paper: "#1a1a2e",
   },
 },
 typography: {
   fontFamily: "'Poppins', sans-serif",
 },
 components: {
   MuiDialog: {
     styleOverrides: {
       paper: {
         borderRadius: 20,
         paddingTop: 8,
         background: "linear-gradient(145deg, #1a1a2e 0%, #16213e 50%, #1a1a2e 100%)",
         border: "1px solid rgba(139,92,246,0.2)",
         boxShadow: "0 25px 80px rgba(0,0,0,0.6), 0 0 40px rgba(139,92,246,0.1)",
       },
     },
   },
   MuiButton: {
     styleOverrides: {
       root: {
         borderRadius: 12,
         textTransform: "none",
         fontWeight: 600,
       },
     },
   },
   MuiTextField: {
     styleOverrides: {
       root: {
         '& .MuiOutlinedInput-root': {
           color: '#e2e8f0',
           backgroundColor: 'rgba(15,15,30,0.6)',
           borderRadius: 12,
           '& fieldset': {
             borderColor: 'rgba(139,92,246,0.25)',
           },
           '&:hover fieldset': {
             borderColor: 'rgba(139,92,246,0.5)',
           },
           '&.Mui-focused fieldset': {
             borderColor: '#a78bfa',
             boxShadow: '0 0 15px rgba(139,92,246,0.15)',
           },
         },
         '& .MuiInputLabel-root': {
           color: 'rgba(148,163,184,0.6)',
         },
         '& .MuiInputLabel-root.Mui-focused': {
           color: '#a78bfa',
         },
         '& .MuiFormHelperText-root': {
           color: 'rgba(148,163,184,0.5)',
         },
       },
     },
   },
 },
});

export function AccountLoginDialog({ open, onClose, onLogin, onGuestMode, showGuestButton = true, theme: storeTheme }) {
 const { t } = useTranslation();
 const GST_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
 const [step, setStep] = useState(1);
  const [formValues, setFormValues] = useState({
    name: "",
    phone: "",
    otp: "",
  });
  const [sessionId, setSessionId] = useState("");
  const [otpError, setOtpError] = useState("");
  const [loading, setLoading] = useState(false);
 const [isB2B, setIsB2B] = useState(false); 
 const [gstVerified, setGstVerified] = useState(false);
 const [gstLoading, setGstLoading] = useState(false);
 const isB2BMode = localStorage.getItem('b2b_Price') === 'true';
 const { bussinessUuid } = useParams();
  const themeId = resolveThemeId(storeTheme || {});
  const isMidnight = themeId === "midnight" || themeId === "lunar" ||themeId==="aura"||themeId==="ironforge";
  const muiTheme = isMidnight ? midnightTheme : lightTheme;

 React.useEffect(() => {
 setIsB2B(isB2BMode);
 }, [isB2BMode]);
 // Validation schemas for each step
 const step1ValidationSchema = Yup.object({
 name: Yup.string()
 .required("Full name is required")
 .min(2, "Name must be at least 2 characters"),
 phone: Yup.string()
 .matches(/^[0-9]{10}$/, "Phone number must be exactly 10 digits")
 .required("Phone number is required"),
 gstIN: isB2B
 ? gstVerified
 ? Yup.string().notRequired() 
 : Yup.string()
 .matches(GST_REGEX, "Invalid GST number")
 .required("GST number is required")
 : Yup.string().notRequired(),
 });

  const step2ValidationSchema = Yup.object({
    otp: Yup.string()
      .matches(/^[0-9]{6}$/, "OTP must be exactly 6 digits")
      .required("OTP is required"),
  });

  const handleSendOtp = async (values) => {
    try {
      setLoading(true);
      const response = await sendWhatsAppOtpService(values.phone, values.name);
      if (response.data && response.data.success) {
        setSessionId(response.data.sessionId);
        setFormValues((prev) => ({
          ...values,
          otp: response.data.devOtp || "",
        }));
        setStep(2);
        setOtpError("");
        toast.success("OTP sent to your WhatsApp!");
      } else {
        toast.error(response.data.message || "Failed to send OTP");
      }
    } catch (error) {
      console.error("Send OTP error:", error);
      toast.error("Failed to send OTP. Please check your number.");
    } finally {
      setLoading(false);
    }
  };
  const handleVerifyGST = async (gstIN, form) => {
    try {
      setGstLoading(true);
      const res = await verifyGstForOnlineStore(bussinessUuid, { gstIN });

      if (res?.data?.verified === true) {
        setGstVerified(true);
        form.setFieldError("gstIN", undefined);
        form.setFieldTouched("gstIN", false, false);
      } else {
        setGstVerified(false);
        form.setFieldError("gstIN", "GST number not verified");
      }
    } catch (err) {
      setGstVerified(false);
      form.setFieldError("gstIN", "GST verification failed");
    } finally {
      setGstLoading(false);
    }
  };
  const handleVerifyOtp = async (values) => {
    try {
      setLoading(true);
      const fingerprint = await getFingerprint();
      const payload = {
        sessionId,
        otp: values.otp,
        name: formValues.name,
        fingerprint,
      };

      const response = await whatsappLoginService(bussinessUuid, payload);

      if (response.data && response.data.success) {
        const { customer, token } = response.data;

        // Map backend customer to what the frontend expects
        const mappedUser = {
          ...customer,
          phone: customer.phone || customer.customer_mobile,
          name: customer.name,
        };

        if (token) {
          localStorage.setItem("customerAuthToken", token);
        }

          await onLogin(mappedUser);
        handleClose();
        toast.success("Login successful!");
      } else {
        setOtpError(response.data.message || "Invalid OTP");
      }
    } catch (error) {
      console.error("Verify OTP error:", error);
      setOtpError("Invalid OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
    setStep(1);
    setFormValues({ name: "", phone: "", otp: "" });
    setOtpError("");
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    } else {
      handleClose();
    }
  };

  const handleResend = async () => {
    if (formValues.phone) {
      await handleSendOtp(formValues);
    }
  };

 // ─── Midnight (dark) version ─────────────────────────────────────────
 if (isMidnight) {
   return (
   <ThemeProvider theme={muiTheme}>
   <Dialog
   open={open}
   onClose={handleClose}
   maxWidth="sm"
   fullWidth
   sx={{
     '& .MuiBackdrop-root': {
       backgroundColor: 'rgba(10,10,20,0.75)',
       backdropFilter: 'blur(8px)',
     }
   }}
   >
   {/* Header */}
   <Box
   sx={{
   py: 3,
   textAlign: "center",
   position: "relative",
   borderBottom: "1px solid rgba(139,92,246,0.15)",
   background: "linear-gradient(180deg, rgba(26,26,46,0.95) 0%, rgba(22,33,62,0.95) 100%)",
   }}
   >
   <IconButton
   onClick={handleClose}
   sx={{
     position: "absolute",
     right: 16,
     top: 16,
     color: "rgba(148,163,184,0.6)",
     '&:hover': {
       color: "#a78bfa",
       backgroundColor: "rgba(139,92,246,0.1)",
     }
   }}
   >
   <FaTimes />
   </IconButton>
   <Typography
   variant="h5"
   fontWeight="700"
   sx={{
     background: "linear-gradient(135deg, #e2e8f0 0%, #c084fc 50%, #a78bfa 100%)",
     WebkitBackgroundClip: "text",
     WebkitTextFillColor: "transparent",
     backgroundClip: "text",
   }}
   >
   {t("ACCOUNT_LOGIN.WELCOME_BACK")}
   </Typography>
   <Typography
   variant="body2"
   sx={{ mt: 0.5, color: "rgba(148,163,184,0.6)" }}
   >
   {step === 1
   ? t("ACCOUNT_LOGIN.SIGN_IN_FOR_A_BETTER_SHOPPING_EXPERIENCE")
   : t("ACCOUNT_LOGIN.VERIFY_YOUR_IDENTITY")}
   </Typography>
   </Box>

   {/* Step Progress */}
   <Box sx={{
     display: "flex",
     justifyContent: "center",
     mt: 2.5,
     mb: 1,
     gap: 1,
   }}>
   {[1, 2].map((s) => (
   <Box
   key={s}
   sx={{
     width: step === s ? 24 : 10,
     height: 10,
     borderRadius: "999px",
     transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
     background: step === s
       ? "linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)"
       : "rgba(139,92,246,0.15)",
     boxShadow: step === s ? "0 0 12px rgba(139,92,246,0.4)" : "none",
   }}
   />
   ))}
   </Box>

   <DialogContent sx={{ px: 4, py: 3, background: "transparent" }}>
   {/* Step 1: Name + Phone */}
   {step === 1 && (
   <Formik
   initialValues={{ name: formValues.name, phone: formValues.phone ,gstIN:formValues.gstIN}}
   validationSchema={step1ValidationSchema}
   onSubmit={handleSendOtp}
   >
   {({ isValid, touched, errors }) => (
   <Form id="account-login-step1-form">
   <motion.div
   initial={{ opacity: 0, y: 20 }}
   animate={{ opacity: 1, y: 0 }}
   >
   <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
   <FaUser style={{ marginRight: 8, color: "#a78bfa" }} />
   <Field name="name">
   {({ field }) => (
   <TextField
   {...field}
   autoFocus
   label={t("ACCOUNT_LOGIN.FULL_NAME")}
   fullWidth
   variant="outlined"
   error={touched.name && Boolean(errors.name)}
   helperText={(touched.name && errors.name) ||
   t("ACCOUNT_LOGIN.FULL_NAME_IS_REQUIRED")
   }
   />
   )}
   </Field>
   </Box>
   <Box sx={{ display: "flex", alignItems: "center" }}>
   <FaPhoneAlt style={{ marginRight: 8, color: "#a78bfa" }} />
   <Field name="phone">
   {({ field, form }) => (
   <TextField
   {...field}
   label={t("ACCOUNT_LOGIN.MOBILE_NUMBER")}
   type="tel"
   fullWidth
   variant="outlined"
   onChange={(e) => {
   const value = e.target.value.replace(/\D/, "");
   form.setFieldValue("phone", value);
   }}
   inputProps={{
   maxLength: 10,
   pattern: "[0-9]{10}",
   inputMode: "numeric",
   }}
   error={touched.phone && Boolean(errors.phone)}
   helperText={
   (touched.phone && errors.phone) ||
   t("ACCOUNT_LOGIN.ENTER_YOUR_10_DIGIT_NUMBER")
   }
   />
   )}
   </Field>
   </Box>
   {isB2B && (
   <Box sx={{ mt: 2 }}>
   <Field name="gstIN">
   {({ field, form }) => (
   <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, marginLeft:3.5 }}>
   <TextField
   {...field}
   label="GST Number"
   fullWidth
   variant="outlined"
   onChange={(e) => {
   form.setFieldValue("gstIN", e.target.value.toUpperCase());
   setGstVerified(false);
   }}
   error={Boolean(form.touched.gstIN && form.errors.gstIN)}
   helperText={
   (form.touched.gstIN && form.errors.gstIN) ||
   "Enter 15-digit GST number"
   }
   />
   <Button
   variant="outlined"
   disabled={gstLoading || !form.values.gstIN}
   onClick={() => handleVerifyGST(form.values.gstIN, form)}
   sx={{
   height: "56px",
   minWidth: "110px",
   whiteSpace: "nowrap",
   borderColor: "rgba(139,92,246,0.3)",
   color: "#a78bfa",
   '&:hover': {
     borderColor: "#a78bfa",
     backgroundColor: "rgba(139,92,246,0.1)",
   }
   }}
   >
   {gstLoading ? "Verifying..." : "Verify"}
   </Button>
   </Box>
   )}
   </Field>
   </Box>
  )}
   </motion.div>
   <button type="submit" style={{ display: "none" }} />
   </Form>
   )}
   </Formik>
   )}

   {/* Step 2: OTP */}
   {step === 2 && (
   <Formik
   initialValues={{ otp: formValues.otp }}
   validationSchema={step2ValidationSchema}
   onSubmit={handleVerifyOtp}
   >
   {({ isValid, touched, errors, values }) => (
   <Form id="account-login-step2-form">
   <motion.div
   initial={{ opacity: 0, y: 20 }}
   animate={{ opacity: 1, y: 0 }}
   >
   <Typography
   sx={{ mb: 2, textAlign: "center", color: "rgba(203,213,225,0.8)" }}
   >
   {t("ACCOUNT_LOGIN.WE_'VE_SENT_A_VERIFICATION_CODE_TO")} <br />
   <b style={{ color: "#c084fc" }}>+91 {formValues.phone}</b>
   </Typography>
   <Box sx={{ display: "flex", alignItems: "center" }}>
   <FaKey style={{ marginRight: 8, color: "#a78bfa" }} />
   <Field name="otp">
   {({ field, form }) => (
   <TextField
   {...field}
   autoFocus
   label={t("ACCOUNT_LOGIN.ENTER_OTP")}
   type="tel"
   fullWidth
   variant="outlined"
   onChange={(e) => {
   const value = e.target.value.replace(/\D/, "");
   form.setFieldValue("otp", value);
   }}
                    inputProps={{
                      maxLength: 6,
                      pattern: "[0-9]{6}",
                      inputMode: "numeric",
                    }}
                    error={
                      (touched.otp && Boolean(errors.otp)) ||
                      Boolean(otpError)
                    }
                    helperText={
                      (touched.otp && errors.otp) ||
                      otpError ||
                      t("ACCOUNT_LOGIN.6_DIGIT_VERIFICATION_CODE") || "6-digit verification code"
                    }
   />
   )}
   </Field>
   </Box>
    <Typography
      variant="body2"
      sx={{
        mt: 2,
        textAlign: "center",
        fontWeight: 600,
        cursor: loading ? "not-allowed" : "pointer",
        opacity: loading ? 0.5 : 1,
        color: "#a78bfa",
        '&:hover': { color: "#c084fc" },
        transition: "color 0.3s ease",
      }}
      onClick={!loading ? handleResend : undefined}
    >
      {loading ? "Please wait..." : t("ACCOUNT_LOGIN.DIDN'T_RECIEVE_THE_CODE_RESEND")}
    </Typography>
   </motion.div>
   <button type="submit" style={{ display: "none" }} />
   </Form>
   )}
   </Formik>
   )}
   </DialogContent>

   {/* Footer Actions */}
  <DialogActions
    sx={{
      px: 4,
      pb: 3,
      flexDirection: { xs: "column", sm: "row" },
      gap: 1.5,
      background: "transparent",
      borderTop: "1px solid rgba(139,92,246,0.1)",
    }}
  >
    {step === 1 && showGuestButton && (
      <Button
        onClick={onGuestMode}
        color="secondary"
        variant="outlined"
        fullWidth={window.innerWidth < 600}
        size="large"
        sx={{
          borderColor: "rgba(139,92,246,0.3)",
          color: "#c084fc",
          '&:hover': {
            borderColor: "#a78bfa",
            backgroundColor: "rgba(139,92,246,0.1)",
          }
        }}
      >
        {t("ACCOUNT_LOGIN.CONTINUE_AS_GUEST")}
      </Button>
    )}
    
    <Button
      onClick={handleBack}
      fullWidth={window.innerWidth < 600}
      size="large"
      sx={{
        color: "rgba(203,213,225,0.6)",
        '&:hover': {
          color: "#e2e8f0",
          backgroundColor: "rgba(255,255,255,0.05)",
        }
      }}
    >
      {step === 1 ? t("ACCOUNT_LOGIN.CANCEL") : t("ACCOUNT_LOGIN.BACK")}
    </Button>
    
    {step === 1 && (
      <Button
        variant="contained"
        disabled={isB2B && !gstVerified || loading}
        type="submit"
      form="account-login-step1-form"
        fullWidth={window.innerWidth < 600}
        size="large"
        sx={{
          background: "linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)",
          boxShadow: "0 4px 20px rgba(139,92,246,0.3)",
          color: "#fff",
          '&:hover': {
            background: "linear-gradient(135deg, #7c3aed 0%, #9333ea 100%)",
            boxShadow: "0 6px 30px rgba(139,92,246,0.45)",
          },
          '&.Mui-disabled': {
            background: "rgba(139,92,246,0.2)",
            color: "rgba(255,255,255,0.3)",
          }
        }}
      >
        {loading ? "Sending..." : t("ACCOUNT_LOGIN.SEND_OTP")}
      </Button>
    )}
    
    {step === 2 && (
      <Button
        variant="contained"
        disabled={loading}
         type="submit"
      form="account-login-step2-form"
        fullWidth={window.innerWidth < 600}
        size="large"
        sx={{
          background: "linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)",
          boxShadow: "0 4px 20px rgba(139,92,246,0.3)",
          color: "#fff",
          '&:hover': {
            background: "linear-gradient(135deg, #7c3aed 0%, #9333ea 100%)",
            boxShadow: "0 6px 30px rgba(139,92,246,0.45)",
          },
          '&.Mui-disabled': {
            background: "rgba(139,92,246,0.2)",
            color: "rgba(255,255,255,0.3)",
          }
        }}
      >
        {loading ? "Verifying..." : t("ACCOUNT_LOGIN.VERIFY")}
      </Button>
    )}
  </DialogActions>
   </Dialog>
   </ThemeProvider>
   );
 }

 // ─── Default (light) version ─────────────────────────────────────────
 return (
 <ThemeProvider theme={muiTheme}>
 <Dialog
 open={open}
 onClose={handleClose}
 maxWidth="sm"
 fullWidth
 PaperProps={{
 className:
 "bg-white text-gray-900 border border-gray-200 ",
 }}
 >
 {/* Header */}
 <Box
 className="bg-white "
 sx={{
 py: 3,
 textAlign: "center",
 position: "relative",
 borderBottom: "1px solid #e5e7eb",
 }}
 >
 <IconButton
 onClick={handleClose}
 className="text-gray-500 "
 sx={{ position: "absolute", right: 16, top: 16 }}
 >
 <FaTimes />
 </IconButton>
 <Typography
 variant="h5"
 fontWeight="700"
 className="text-gray-900 "
 >
 {t("ACCOUNT_LOGIN.WELCOME_BACK")}
 </Typography>
 <Typography
 variant="body2"
 className="mt-1 text-gray-500 "
 >
 {step === 1
 ? t("ACCOUNT_LOGIN.SIGN_IN_FOR_A_BETTER_SHOPPING_EXPERIENCE")
 : t("ACCOUNT_LOGIN.VERIFY_YOUR_IDENTITY")}
 </Typography>
 </Box>

 {/* Step Progress */}
 <Box sx={{ display: "flex", justifyContent: "center", mt: 2, mb: 1 }}>
 {[1, 2].map((s) => (
 <Box
 key={s}
 className={`w-2.5 h-2.5 rounded-full mx-1 transition ${
 step === s
 ? "bg-blue-600 "
 : "bg-gray-300 "
 }`}
 />
 ))}
 </Box>

        <DialogContent sx={{ px: 4, py: 3 }} className='bg-white '>
          {/* Step 1: Name + Phone */}
          {step === 1 && (
            <Formik
              initialValues={{
                name: formValues.name,
                phone: formValues.phone,
                gstIN: formValues.gstIN,
              }}
              validationSchema={step1ValidationSchema}
              onSubmit={handleSendOtp}
            >
              {({ isValid, touched, errors }) => (
                <Form>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                      <FaUser className='mr-2 text-blue-600 ' />
                      <Field name='name'>
                        {({ field }) => (
                          <TextField
                            {...field}
                            autoFocus
                            label={t("ACCOUNT_LOGIN.FULL_NAME")}
                            fullWidth
                            variant='outlined'
                            className='bg-white text-gray-900 '
                            error={touched.name && Boolean(errors.name)}
                            helperText={
                              (touched.name && errors.name) ||
                              t("ACCOUNT_LOGIN.FULL_NAME_IS_REQUIRED")
                            }
                          />
                        )}
                      </Field>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <FaPhoneAlt className='mr-2 text-blue-600 ' />
                      <Field name='phone'>
                        {({ field, form }) => (
                          <TextField
                            {...field}
                            label={t("ACCOUNT_LOGIN.MOBILE_NUMBER")}
                            type='tel'
                            fullWidth
                            variant='outlined'
                            className='bg-white text-gray-900 '
                            onChange={(e) => {
                              const value = e.target.value.replace(/\D/, "");
                              form.setFieldValue("phone", value);
                            }}
                            inputProps={{
                              maxLength: 10,
                              pattern: "[0-9]{10}",
                              inputMode: "numeric",
                            }}
                            error={touched.phone && Boolean(errors.phone)}
                            helperText={
                              (touched.phone && errors.phone) ||
                              t("ACCOUNT_LOGIN.ENTER_YOUR_10_DIGIT_NUMBER")
                            }
                          />
                        )}
                      </Field>
                    </Box>
                    {isB2B && (
                      <Box sx={{ mt: 2 }}>
                        <Field name='gstIN'>
                          {({ field, form }) => (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 1.5,
                                marginLeft: 3.5,
                              }}
                            >
                              {/* GST INPUT */}
                              <TextField
                                {...field}
                                label='GST Number'
                                fullWidth
                                variant='outlined'
                                onChange={(e) => {
                                  form.setFieldValue(
                                    "gstIN",
                                    e.target.value.toUpperCase(),
                                  );
                                  setGstVerified(false);
                                }}
                                error={Boolean(
                                  form.touched.gstIN && form.errors.gstIN,
                                )}
                                helperText={
                                  (form.touched.gstIN && form.errors.gstIN) ||
                                  "Enter 15-digit GST number"
                                }
                              />

                              {/* VERIFY BUTTON */}
                              <Button
                                variant='outlined'
                                disabled={gstLoading || !form.values.gstIN}
                                onClick={() =>
                                  handleVerifyGST(form.values.gstIN, form)
                                }
                                sx={{
                                  height: "56px",
                                  minWidth: "110px",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {gstLoading ? "Verifying..." : "Verify"}
                              </Button>
                            </Box>
                          )}
                        </Field>
                      </Box>
                    )}
                  </motion.div>

                  {/* Hidden submit button for Enter key functionality */}
                  <button type='submit' style={{ display: "none" }} />
                </Form>
              )}
            </Formik>
          )}

          {/* Step 2: OTP */}
          {step === 2 && (
            <Formik
              initialValues={{ otp: formValues.otp }}
              validationSchema={step2ValidationSchema}
              onSubmit={handleVerifyOtp}
            >
              {({ isValid, touched, errors, values }) => (
                <Form>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <Typography
                      sx={{ mb: 2, textAlign: "center" }}
                      className='text-gray-700 '
                    >
                      {t("ACCOUNT_LOGIN.WE_'VE_SENT_A_VERIFICATION_CODE_TO")}{" "}
                      <br />
                      <b>+91 {formValues.phone}</b>
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <FaKey className='mr-2 text-blue-600 ' />
                      <Field name='otp'>
                        {({ field, form }) => (
                          <TextField
                            {...field}
                            autoFocus
                            label={t("ACCOUNT_LOGIN.ENTER_OTP")}
                            type='tel'
                            fullWidth
                            variant='outlined'
                            className='bg-white text-gray-900 '
                            onChange={(e) => {
                              const value = e.target.value.replace(/\D/, "");
                              form.setFieldValue("otp", value);
                            }}
                            inputProps={{
                              maxLength: 6,
                              pattern: "[0-9]{6}",
                              inputMode: "numeric",
                            }}
                            error={
                              (touched.otp && Boolean(errors.otp)) ||
                              Boolean(otpError)
                            }
                            helperText={
                              (touched.otp && errors.otp) ||
                              otpError ||
                              t("ACCOUNT_LOGIN.6_DIGIT_VERIFICATION_CODE") ||
                              "6-digit verification code"
                            }
                          />
                        )}
                      </Field>
                    </Box>
                    <Typography
                      variant='body2'
                      color='primary'
                      sx={{
                        mt: 2,
                        textAlign: "center",
                        fontWeight: 600,
                        cursor: loading ? "not-allowed" : "pointer",
                        opacity: loading ? 0.5 : 1,
                      }}
                      className='text-blue-600'
                      onClick={!loading ? handleResend : undefined}
                    >
                      {loading
                        ? "Please wait..."
                        : t("ACCOUNT_LOGIN.DIDN'T_RECIEVE_THE_CODE_RESEND")}
                    </Typography>
                  </motion.div>

                  {/* Hidden submit button for Enter key functionality */}
                  <button type='submit' style={{ display: "none" }} />
                </Form>
              )}
            </Formik>
          )}
        </DialogContent>

        {/* Footer Actions */}
        <DialogActions
          sx={{
            px: 4,
            pb: 3,
            flexDirection: { xs: "column", sm: "row" },
            gap: 1.5,
          }}
          className='bg-white '
        >
          {step === 1 && showGuestButton && (
            <Button
              onClick={onGuestMode}
              color='secondary'
              variant='outlined'
              fullWidth={window.innerWidth < 600}
              size='large'
              className='text-gray-700 border border-gray-300 '
            >
              {t("ACCOUNT_LOGIN.CONTINUE_AS_GUEST")}
            </Button>
          )}

          <Button
            onClick={handleBack}
            fullWidth={window.innerWidth < 600}
            size='large'
            className='text-gray-700 '
          >
            {step === 1 ? t("ACCOUNT_LOGIN.CANCEL") : t("ACCOUNT_LOGIN.BACK")}
          </Button>

          {step === 1 && (
            <Button
              variant='contained'
              disabled={(isB2B && !gstVerified) || loading}
              onClick={() => {
                // Programmatically submit the form
                const form = document.querySelector("form");
                if (form) form.requestSubmit();
              }}
              fullWidth={window.innerWidth < 600}
              size='large'
              className='bg-blue-600 hover:bg-blue-700 text-white'
            >
              {loading ? "Sending..." : t("ACCOUNT_LOGIN.SEND_OTP")}
            </Button>
          )}

          {step === 2 && (
            <Button
              variant='contained'
              disabled={loading}
              onClick={() => {
                // Programmatically submit the form
                const form = document.querySelector("form");
                if (form) form.requestSubmit();
              }}
              fullWidth={window.innerWidth < 600}
              size='large'
              className='bg-blue-600 hover:bg-blue-700 text-white'
            >
              {loading ? "Verifying..." : t("ACCOUNT_LOGIN.VERIFY")}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
}
